/**
 * EditorToolbar — top bar for the Profile Generator editor.
 * Back + profile-name rename on the left; undo/redo, zoom, view toggles and the
 * save/export/template actions on the right. All view toggles drive
 * canvasConfig through `actions.updateCanvas`.
 */
import React from 'react';
import {
  ChevronLeft, Undo2, Redo2, Minus, Plus, Grid3x3, Square, Magnet,
  LayoutTemplate, Download, Save,
} from 'lucide-react';
import { T } from '../../../components/em/tokens';
import { ZOOM_MIN, ZOOM_MAX } from '../pgConstants';

const clampZoom = (z) => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Math.round(z * 100) / 100));

const Divider = () => (
  <span style={{ width: 1, alignSelf: 'stretch', margin: '0 4px', background: T.border }} />
);

// Square icon button. `active` paints the em-orange selected state; `disabled`
// dims and blocks the click.
function TBtn({ icon: Icon, title, onClick, active, disabled, className }) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      data-tip={title}
      disabled={disabled}
      onClick={onClick}
      className={className}
      style={{
        width: 32, height: 32, flexShrink: 0,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        borderRadius: T.rMd,
        border: `1px solid ${active ? T.orange : T.border}`,
        background: active ? T.orange50 : 'transparent',
        color: disabled ? T.fg7 : active ? T.orange : T.fg4,
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <Icon size={15} />
    </button>
  );
}

export default function EditorToolbar({
  profileName, onRename, onBack, onSave, saving, dirty, onExport, onSaveTemplate,
  undo, redo, canUndo, canRedo, canvasConfig, actions,
}) {
  const zoom = canvasConfig?.zoom ?? 1;

  return (
    <div
      className="pg-toolbar"
      style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '8px 12px',
        background: T.bgCard,
        borderBottom: `1px solid ${T.border}`,
      }}
    >
      {/* Left: back + rename */}
      <button
        type="button"
        onClick={onBack}
        className="pg-toolbar-back"
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 4,
          padding: '6px 10px 6px 6px', borderRadius: T.rMd,
          border: `1px solid ${T.border}`, background: 'transparent',
          color: T.fg4, cursor: 'pointer', font: `500 13px/1 ${T.font}`,
          flexShrink: 0,
        }}
      >
        <ChevronLeft size={16} />Profiles
      </button>

      <input
        value={profileName ?? ''}
        onChange={(e) => onRename(e.target.value)}
        placeholder="Untitled profile"
        aria-label="Profile name"
        className="pg-toolbar-name"
        style={{
          minWidth: 80, maxWidth: 280, flex: '0 1 auto',
          border: 0, outline: 'none', background: 'transparent',
          color: T.fg1, font: `700 15px/1.2 ${T.font}`,
          padding: '4px 6px', borderRadius: T.rSm,
        }}
      />

      {/* Spacer pushes the rest to the right */}
      <span style={{ flex: 1 }} />

      {/* Undo / redo */}
      <TBtn icon={Undo2} title="Undo" onClick={undo} disabled={!canUndo} className="pg-undo" />
      <TBtn icon={Redo2} title="Redo" onClick={redo} disabled={!canRedo} className="pg-redo" />

      <Divider />

      {/* Zoom */}
      <TBtn
        icon={Minus}
        title="Zoom out"
        onClick={() => actions.updateCanvas({ zoom: clampZoom(zoom - 0.25) }, false)}
        disabled={zoom <= ZOOM_MIN}
      />
      <span style={{
        minWidth: 46, textAlign: 'center', color: T.fg3,
        font: `600 12px/1 ${T.font}`, userSelect: 'none',
      }}>
        {Math.round(zoom * 100)}%
      </span>
      <TBtn
        icon={Plus}
        title="Zoom in"
        onClick={() => actions.updateCanvas({ zoom: clampZoom(zoom + 0.25) }, false)}
        disabled={zoom >= ZOOM_MAX}
      />

      <Divider />

      {/* View toggles */}
      <TBtn
        icon={Grid3x3}
        title="Toggle grid"
        active={!!canvasConfig?.showGrid}
        onClick={() => actions.updateCanvas({ showGrid: !canvasConfig.showGrid })}
      />
      <TBtn
        icon={Square}
        title="Toggle safe margin"
        active={!!canvasConfig?.showSafeMargin}
        onClick={() => actions.updateCanvas({ showSafeMargin: !canvasConfig.showSafeMargin })}
      />
      <TBtn
        icon={Magnet}
        title="Snap to grid"
        active={!!canvasConfig?.snapToGrid}
        onClick={() => actions.updateCanvas({ snapToGrid: !canvasConfig.snapToGrid })}
      />

      <Divider />

      {/* Template / export */}
      <button
        type="button"
        onClick={onSaveTemplate}
        className="pg-template-btn"
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '7px 12px', borderRadius: T.rMd,
          border: `1px solid ${T.border}`, background: 'transparent',
          color: T.fg2, cursor: 'pointer', font: `500 13px/1 ${T.font}`,
          flexShrink: 0,
        }}
      >
        <LayoutTemplate size={15} />Template
      </button>
      <button
        type="button"
        onClick={onExport}
        className="pg-export-btn"
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '7px 12px', borderRadius: T.rMd,
          border: `1px solid ${T.border}`, background: 'transparent',
          color: T.fg2, cursor: 'pointer', font: `500 13px/1 ${T.font}`,
          flexShrink: 0,
        }}
      >
        <Download size={15} />Export
      </button>

      {/* Save (primary) */}
      <button
        type="button"
        onClick={onSave}
        disabled={saving}
        className="pg-save-btn"
        style={{
          position: 'relative',
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '8px 16px', borderRadius: T.rMd, border: 0,
          background: T.gradient, color: '#fff',
          cursor: saving ? 'default' : 'pointer',
          font: `600 13px/1 ${T.font}`,
          boxShadow: T.shadowBrand, opacity: saving ? 0.75 : 1,
          flexShrink: 0,
        }}
      >
        <Save size={15} />{saving ? 'Saving…' : 'Save'}
        {dirty && !saving && (
          <span
            title="Unsaved changes"
            style={{
              position: 'absolute', top: -3, right: -3,
              width: 9, height: 9, borderRadius: '50%',
              background: T.warn, border: '2px solid #fff',
            }}
          />
        )}
      </button>
    </div>
  );
}
